import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Define cleanup first since it's used by connect
  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Define handleNotification
  const handleNotification = useCallback((data) => {
    const { type, severity, message, data: notifData } = data;

    const toastType = {
      critical: 'error',
      warning: 'warning',
      success: 'success',
      info: 'info'
    }[severity] || 'info';

    switch (type) {
      case 'compliance_drift':
        toast[toastType](message, {
          description: `${notifData?.framework} is at ${notifData?.compliance?.toFixed(1)}% compliance`,
          duration: 10000,
          action: {
            label: 'View',
            onClick: () => { window.location.href = '/compliance-drift'; }
          }
        });
        break;

      case 'risk_alert':
        toast[toastType](message, {
          description: `Risk Score: ${notifData?.score}`,
          duration: 8000,
          action: {
            label: 'View',
            onClick: () => { window.location.href = '/risks'; }
          }
        });
        break;

      case 'control_status_change':
        toast[toastType](message, {
          description: `${notifData?.framework}: ${notifData?.control_code}`,
          duration: 5000,
          action: {
            label: 'View',
            onClick: () => { window.location.href = '/controls'; }
          }
        });
        break;

      case 'system_alert':
        toast[toastType](message, { duration: 10000 });
        break;

      default:
        if (message) {
          toast[toastType](message);
        }
    }
  }, []);

  // Define connect with dependencies
  const connect = useCallback(() => {
    if (!user?.id) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/${user.id}?org_id=${user.organization_id || ''}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data === 'pong' || event.data === 'pong') return;
          setLastMessage(data);
          handleNotification(data);
        } catch (e) {
          if (event.data !== 'pong') {
            console.log('WebSocket message:', event.data);
          }
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        cleanup();
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (user?.id) {
            connect();
          }
        }, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [user, cleanup, handleNotification]);

  const disconnect = useCallback(() => {
    cleanup();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [cleanup]);

  useEffect(() => {
    if (user?.id) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [user?.id, connect, disconnect]);

  const value = {
    isConnected,
    lastMessage,
    connect,
    disconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
